import type {
  AttachmentInfoResponse,
  AttachmentItem,
  AttachmentPresignedUrl,
  AttachmentsResponse,
  DeleteAttachmentProps,
  GetAttachmentsProps,
  GetQuestionnaireAttachmentsProps,
  PostAttachmentStagingProps,
  PostAttachmentStagingResponse,
} from '../../@types/attachments';
import type {
  BackgroundJobDetailsResult,
  CreateImportJobResult,
  StartImportJobApiArgs,
} from '../../@types/background-jobs';
import type { ResultValidationResponse } from '../../@types/common';
import { api } from './api';

export const fileApi = api.enhanceEndpoints({ addTagTypes: ['File', 'QuestionnaireFile'] }).injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getFilesByRecordId: builder.query<Array<AttachmentItem>, GetAttachmentsProps>({
      query: ({ recordId }) => `/records/${recordId}/files`,
      transformResponse: (response: AttachmentsResponse) => {
        return response?.result?.filter((item) => item?.deleted !== true) || [];
      },
      providesTags: (result, error, { recordId }) =>
        result
          ? [
              ...result.map((item) => ({
                type: 'File' as const,
                id: item.id,
              })),
              { type: 'File', id: `${recordId}-LIST` },
            ]
          : [{ type: 'File', id: `${recordId}-LIST` }],
    }),
    getFilesByRecordIdAndQuestionId: builder.query<Array<AttachmentItem>, GetQuestionnaireAttachmentsProps>({
      query: ({ recordId, questionId }) => `/records/${recordId}/questions/${questionId}/files`,
      transformResponse: (response: AttachmentsResponse) => {
        return response?.result || [];
      },
      providesTags: (result, error, { recordId, questionId }) =>
        result
          ? [
              ...result.map((item) => ({
                type: 'QuestionnaireFile' as const,
                id: item.id,
              })),
              { type: 'QuestionnaireFile', id: `${recordId}-${questionId}` },
            ]
          : [{ type: 'QuestionnaireFile', id: `${recordId}-${questionId}` }],
    }),
    addFileToStaging: builder.mutation<string | undefined, PostAttachmentStagingProps>({
      query: ({ formData }) => ({
        url: `/files/staging`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: PostAttachmentStagingResponse) => {
        return response.result;
      },
    }),
    getPresignedUrl: builder.query<string, number>({
      // number used to invalidate cache
      query: () => '/files/staging/url',
      transformResponse: (response: AttachmentPresignedUrl) => {
        return response.result;
      },
    }),
    deleteAttachment: builder.mutation<number, DeleteAttachmentProps>({
      query: ({ id }) => ({
        url: `/files/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { recordId, questionId }) => {
        if (questionId) {
          return [
            { type: 'QuestionnaireFile', id: `${recordId}-${questionId}` },
            { type: 'File', id: `${recordId}-LIST` },
          ];
        } else {
          return [{ type: 'File', id: `${recordId}-LIST` }];
        }
      },
    }),
    getFileUrl: builder.query<string, string>({
      query: (sourceId) => `/files/attachment/${sourceId}/url`,
      transformResponse: (response: AttachmentInfoResponse) => response.result,
    }),
    createImportAttachmentsJob: builder.mutation<CreateImportJobResult, void>({
      query: () => ({
        url: `/background-jobs/import-attachments`,
        method: 'POST',
      }),
      transformResponse: (response: ResultValidationResponse<CreateImportJobResult>) => response.result,
    }),
    startImportAttachmentsJob: builder.mutation<BackgroundJobDetailsResult, StartImportJobApiArgs>({
      query: ({ jobId, prefix }) => ({
        url: `/background-jobs/import-attachments/${jobId}/start`,
        method: 'POST',
        params: { prefix: prefix },
      }),
      transformResponse: (response: ResultValidationResponse<BackgroundJobDetailsResult>) => response.result,
    }),
    getImportAttachmentsJobDetails: builder.query<BackgroundJobDetailsResult, number>({
      query: (jobId) => `/background-jobs/import-attachments/${jobId}`,
      transformResponse: (response: ResultValidationResponse<BackgroundJobDetailsResult>) => response.result,
    }),
  }),
});

export const {
  useGetFilesByRecordIdQuery,
  useGetFilesByRecordIdAndQuestionIdQuery,
  useLazyGetFilesByRecordIdAndQuestionIdQuery,
  useAddFileToStagingMutation,
  useDeleteAttachmentMutation,
  useLazyGetFileUrlQuery,
  useLazyGetPresignedUrlQuery,
  useCreateImportAttachmentsJobMutation,
  useStartImportAttachmentsJobMutation,
  useGetImportAttachmentsJobDetailsQuery,
} = fileApi;
